import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Icon from '../components/AppIcon';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Grid Balance admin email - only this user can access
  const ADMIN_EMAIL = 'admin@gridbalance.de';

  const getErrorMessage = (error) => {
    const errorMsg = error?.message || '';
    
    // Network errors
    if (errorMsg?.includes('Failed to fetch') || errorMsg?.includes('NetworkError')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    
    // Supabase specific errors
    if (errorMsg?.includes('Invalid login credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    if (errorMsg?.includes('Email not confirmed')) {
      return 'Please confirm your email address before signing in.';
    }
    
    if (errorMsg?.includes('User not found')) {
      return 'No account found with this email address.';
    }
    
    if (errorMsg?.includes('Too many requests')) {
      return 'Too many login attempts. Please wait a few minutes and try again.';
    }
    
    if (errorMsg?.includes('Access denied')) {
      return 'Access denied. Only Grid Balance admin can sign in.';
    }
    
    // Generic error
    return errorMsg || 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    
    console.log('\n╔════════════════════════════════════════╗');
    console.log('║     LOGIN FORM SUBMISSION STARTED      ║');
    console.log('╚════════════════════════════════════════╝');
    console.log('Timestamp:', new Date()?.toISOString());
    console.log('Email input:', email);
    console.log('Password length:', password?.length || 0);
    
    // Validation
    console.log('\n🔍 CLIENT-SIDE VALIDATION:');
    if (!email || !password) {
      console.error('❌ Validation failed: Empty fields');
      setError('Please enter both email and password.');
      return;
    }
    console.log('✅ Fields not empty');
    
    if (!email?.includes('@')) {
      console.error('❌ Validation failed: Invalid email format');
      setError('Please enter a valid email address.');
      return;
    }
    console.log('✅ Email format valid');
    
    const normalizedEmail = email?.toLowerCase()?.trim();
    console.log('Normalized email:', normalizedEmail);
    console.log('Expected admin email:', ADMIN_EMAIL);
    console.log('Email match:', normalizedEmail === ADMIN_EMAIL);
    
    if (normalizedEmail !== ADMIN_EMAIL) {
      console.error('❌ Validation failed: Not admin email');
      setError('Access denied. Only Grid Balance admin can sign in.');
      return;
    }
    console.log('✅ Admin email validated');
    
    console.log('\n🔄 Setting loading state...');
    setLoading(true);
    console.log('✅ Loading state set to true');

    try {
      console.log('\n📞 Calling signIn from AuthContext...');
      console.log('signIn function type:', typeof signIn);
      console.log('signIn function exists:', !!signIn);
      
      if (!signIn) {
        console.error('❌ CRITICAL: signIn function not available');
        throw new Error('Authentication system not initialized');
      }
      
      const startTime = performance.now();
      const result = await signIn(email, password);
      const endTime = performance.now();
      const duration = (endTime - startTime)?.toFixed(2);
      
      console.log(`\n⏱️ signIn call completed in ${duration}ms`);
      console.log('Result received:', {
        hasResult: !!result,
        hasData: !!result?.data,
        hasUser: !!result?.data?.user,
        hasError: !!result?.error,
        userEmail: result?.data?.user?.email || 'none',
        errorMessage: result?.error?.message || 'none'
      });
      
      if (result?.error) {
        console.error('❌ Login failed with error:', result?.error);
        const errorMsg = getErrorMessage(result?.error);
        console.log('Displaying error to user:', errorMsg);
        setError(errorMsg);
      } else if (result?.data?.user) {
        // Successful login
        console.log('✅ LOGIN SUCCESSFUL!');
        console.log('User:', result?.data?.user?.email);
        console.log('User ID:', result?.data?.user?.id);
        console.log('\n🧭 Navigating to /admin-dashboard...');
        navigate('/admin-dashboard');
        console.log('✅ Navigation triggered');
      } else {
        console.error('❌ Unexpected result: No error but no user data');
        console.log('Full result:', JSON.stringify(result, null, 2));
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ EXCEPTION in handleSubmit:', {
        message: err?.message,
        name: err?.name,
        stack: err?.stack
      });
      const errorMsg = getErrorMessage(err);
      console.log('Displaying exception error to user:', errorMsg);
      setError(errorMsg);
    } finally {
      console.log('\n🔄 Resetting loading state...');
      setLoading(false);
      console.log('✅ Loading state set to false');
      console.log('\n╔════════════════════════════════════════╗');
      console.log('║     LOGIN FORM SUBMISSION ENDED        ║');
      console.log('╚════════════════════════════════════════╝\n');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-modal border border-border p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Icon name="Car" size={32} color="var(--color-primary)" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Grid Balance Admin
            </h1>
            <p className="text-muted-foreground">
              Bitte einloggen
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">Error</p>
                  <p className="text-sm text-destructive/80 mt-1">{error}</p>
                </div>
                <button 
                  onClick={() => setError('')}
                  className="p-1 rounded hover:bg-destructive/20 transition-smooth"
                >
                  <Icon name="X" size={16} color="var(--color-destructive)" />
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                placeholder="admin@gridbalance.de"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted transition-smooth"
                >
                  <Icon 
                    name={showPassword ? 'EyeOff' : 'Eye'} 
                    size={18} 
                    color="var(--color-muted-foreground)" 
                  />
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full"
              disabled={loading}
              iconName=""
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-center text-muted-foreground">
              Nur für Grid Balance UG Admins
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;